import React, { useState, useEffect } from "react";
import { observer } from "mobx-react";
import { useHistory } from "react-router-dom";
import { agentsData } from "Stores/agentsData";
import { userDetail } from "Stores/userDetail";
import { fetchLinkedInAccounts } from "Utils/commonAPI";
import AgentSelect from "./AgentSelect";
import DayOfWeekField, { DEFAULT_DAYS, scheduleText } from "./DayOfWeekField";
import SequencePickerModal from "./SequencePickerModal";

// The three LinkedIn import sources, with the copy that changes per source.
const SOURCES = {
  search: {
    label: "Sales Navigator search",
    urlLabel: "Sales Navigator search URL",
    placeholder: "https://www.linkedin.com/sales/search/people?...",
    hint: "Paste the URL of a Sales Navigator people search.",
    does: "the people from a Sales Navigator search"
  },
  post: {
    label: "Post engagers",
    urlLabel: "LinkedIn post URL",
    placeholder: "https://www.linkedin.com/posts/...",
    hint: "Imports the people who reacted to or commented on the post.",
    does: "the people who engaged with a post"
  },
  event: {
    label: "Event attendees",
    urlLabel: "LinkedIn event URL",
    placeholder: "https://www.linkedin.com/events/...",
    hint: "Imports the people attending the event.",
    does: "the attendees of an event"
  }
};

const CreateLinkedInImport = observer(() => {
  const history = useHistory();
  const [name, setName] = useState("");
  const [sourceType, setSourceType] = useState("search"); // search | post | event
  const [url, setUrl] = useState("");
  const [askEachRun, setAskEachRun] = useState(true); // default on — import a different post/event each run
  const [engagementType, setEngagementType] = useState("all");
  const [listId, setListId] = useState("");
  const [listName, setListName] = useState("");
  const [sequenceId, setSequenceId] = useState("");
  const [sequenceName, setSequenceName] = useState("");
  const [seqModalOpen, setSeqModalOpen] = useState(false);
  const [runWhen, setRunWhen] = useState("MANUAL"); // MANUAL | DAILY | WEEKLY
  const [time, setTime] = useState("08:00");
  const [days, setDays] = useState(DEFAULT_DAYS);
  const [dailyCap, setDailyCap] = useState(25);
  const [notifyMode, setNotifyMode] = useState("ON_FAILURE");
  const [saving, setSaving] = useState(false);
  const timezone = userDetail.timeZone || "UTC";

  useEffect(() => {
    fetchLinkedInAccounts(); // powers the connect-account warning below
    agentsData.fetchContactLists();
  }, []);

  const src = SOURCES[sourceType];
  const manual = runWhen === "MANUAL";
  const effAsk = manual && askEachRun; // "ask each run" is manual-only
  const urlReady = effAsk || !!url.trim();

  // LinkedIn connection state → a gentle pre-warning (create is still allowed; it's a draft).
  const liAccounts = userDetail.activeLinkedInAccounts || [];
  const hasLinkedIn = liAccounts.length > 0;
  const hasSalesNav = liAccounts.some(a => a && a.linkedinAccountType
    && String(a.linkedinAccountType).toLowerCase() !== "classic");
  const accountWarn = sourceType === "search"
    ? (hasSalesNav ? null : "This source needs a connected Sales Navigator account.")
    : (hasLinkedIn ? null : "This source needs a connected LinkedIn account.");

  const hasDestination = !!listId || !!sequenceId; // imported contacts must land somewhere findable
  const canSubmit = urlReady && hasDestination;

  const buildPayload = () => ({
    name: name.trim() || ("Import from LinkedIn · " + src.label),
    sourceType,
    askEachRun: effAsk,
    url: effAsk ? undefined : url.trim(),
    engagementType: sourceType === "post" ? engagementType : undefined,
    listId: listId || undefined,
    sequenceId: sequenceId || undefined,
    dailyCap: Number(dailyCap),
    notifyMode,
    timezone,
    ...(manual ? {} : { frequency: runWhen, time, ...(runWhen === "WEEKLY" ? { days } : {}) })
  });

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    const agent = await agentsData.createLinkedinImport(buildPayload());
    setSaving(false);
    if (agent && agent.id) history.push(`/agents/${agent.id}`);
  };

  // Open this template's steps in the canvas to tweak before creating (URL can be filled there).
  const customize = async () => {
    setSaving(true);
    const draft = await agentsData.composeTemplate("linkedin-import", buildPayload());
    setSaving(false);
    if (draft) { agentsData.stageDraft(draft); history.push("/agents/new/custom"); }
  };

  const step = node => (<li><span className="ck">✓</span><span>{node}</span></li>);
  const runText = manual ? "when you run it" : scheduleText(runWhen, time, days);

  return (
    <div className="ag-page">
      <div className="ag-page__head">
        <div>
          <button className="ag-link" onClick={() => history.push("/agents/new")}>← Back</button>
          <h1 className="ag-page__title">Hire · Import from LinkedIn</h1>
          <p className="ag-page__sub">Import people from a Sales Navigator search, a post's engagers, or an event's attendees — then enrich and enroll them.</p>
        </div>
      </div>

      <div className="ag-setup">
        <div className="ag-card-panel">
          <div className="ag-fsec">Source</div>
          <label className="ag-field">
            <span className="ag-field__label">Import from<b className="ag-req">required</b></span>
            <AgentSelect
              value={sourceType}
              onChange={v => { setSourceType(v); if (v !== "search") { /* keep url */ } }}
              options={[
                { value: "search", label: "A Sales Navigator search" },
                { value: "post", label: "People who engaged with a post" },
                { value: "event", label: "People attending an event" }
              ]}
            />
          </label>

          {accountWarn && (
            <div className="af-liwarn" style={{ marginBottom: 12 }}>
              <span>{accountWarn}{" "}
                <a className="af-liwarn__link" href="/settings/account/linkedin"
                  onClick={e => { e.preventDefault(); history.push("/settings/account/linkedin"); }}>
                  Connect in Settings → LinkedIn
                </a>
              </span>
            </div>
          )}

          {effAsk ? (
            <div className="ag-field">
              <span className="ag-field__label">{src.urlLabel}</span>
              <div className="af-asknote">You'll be asked for the {src.urlLabel.toLowerCase()} each time you run this agent — so one agent can import a different {sourceType} every time.</div>
            </div>
          ) : (
            <label className="ag-field">
              <span className="ag-field__label">{src.urlLabel}<b className="ag-req">required</b></span>
              <input className="ag-input" value={url} placeholder={src.placeholder} onChange={e => setUrl(e.target.value)} />
              <span className="ag-hint">{src.hint}</span>
            </label>
          )}

          {manual && (
            <label className="ag-check" style={{ display: "flex", alignItems: "center", gap: 8, margin: "2px 0 6px" }}>
              <input type="checkbox" checked={askEachRun} onChange={e => setAskEachRun(e.target.checked)} />
              <span>Ask me for the URL each time I run it</span>
            </label>
          )}

          {sourceType === "post" && (
            <label className="ag-field">
              <span className="ag-field__label">Who to import</span>
              {effAsk && <span className="ag-hint">You can change this each time you run it.</span>}
              <AgentSelect value={engagementType} onChange={setEngagementType} options={[
                { value: "all", label: "Everyone who engaged (reactions + comments)" },
                { value: "reactions", label: "Only people who reacted" },
                { value: "comments", label: "Only people who commented" }
              ]} />
            </label>
          )}

          <label className="ag-field">
            <span className="ag-field__label">Agent name</span>
            <input className="ag-input" value={name} placeholder={"Import from LinkedIn · " + src.label} onChange={e => setName(e.target.value)} />
          </label>

          <div className="ag-fsec">What happens next</div>
          <label className="ag-field">
            <span className="ag-field__label">Add to list{!sequenceId && <b className="ag-req">required</b>}</span>
            <AgentSelect
              searchable placeholder="Select a contact list…"
              value={listId}
              onChange={v => { const o = (agentsData.contactLists || []).find(x => x.id === v); setListId(v); setListName(o ? o.name : v); }}
              options={(agentsData.contactLists || []).map(l => ({ value: l.id, label: l.count ? `${l.name} · ${l.count}` : l.name }))}
            />
            <span className="ag-hint">The imported contacts are added to this list{sequenceId ? " (optional when enrolling into a sequence)" : ""}.</span>
            {listId && (
              <button type="button" className="ag-link" style={{ marginTop: 4 }} onClick={() => { setListId(""); setListName(""); }}>Clear list</button>
            )}
          </label>
          <label className="ag-field">
            <span className="ag-field__label">Enroll into sequence <span className="ag-hint" style={{ fontWeight: 400 }}>(optional)</span></span>
            <button type="button" className="ag-select__btn" onClick={() => setSeqModalOpen(true)}>
              <span className={sequenceName ? "" : "ag-select__ph"}>{sequenceName || "Select a sequence…"}</span>
              <svg className="ag-select__chev" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6" /></svg>
            </button>
            <span className="ag-hint">Email sequences enrich the email before enrolling; undeliverables are dropped.</span>
            {sequenceId && (
              <button type="button" className="ag-link" style={{ marginTop: 4 }} onClick={() => { setSequenceId(""); setSequenceName(""); }}>Clear sequence</button>
            )}
          </label>

          <div className="ag-fsec">Schedule</div>
          <div className="ag-field-row">
            <label className="ag-field"><span className="ag-field__label">Run</span>
              <AgentSelect value={runWhen} onChange={v => { setRunWhen(v); if (v !== "MANUAL") setAskEachRun(false); }} options={[
                { value: "MANUAL", label: "Only when I run it" },
                { value: "DAILY", label: "Every day" },
                { value: "WEEKDAYS", label: "Weekdays (Mon–Fri)" },
                { value: "WEEKLY", label: "Specific days" }
              ]} />
            </label>
            {runWhen === "WEEKLY" && <DayOfWeekField value={days} onChange={setDays} />}
            {!manual && (
              <label className="ag-field"><span className="ag-field__label">At</span>
                <input className="ag-input" type="time" value={time} onChange={e => setTime(e.target.value)} />
              </label>
            )}
            <label className="ag-field"><span className="ag-field__label">Contacts / run</span>
              <input className="ag-input" type="number" min="1" value={dailyCap} onChange={e => setDailyCap(e.target.value)} />
            </label>
          </div>
          {!manual && (
            <span className="ag-hint">A scheduled import needs a fixed URL — “ask each run” only works for manual agents.</span>
          )}

          <div className="ag-fsec">Notifications</div>
          <label className="ag-field">
            <span className="ag-field__label">Notify me</span>
            <AgentSelect value={notifyMode} onChange={setNotifyMode} options={[
              { value: "DAILY_DIGEST", label: "Daily digest — one summary a day" },
              { value: "ON_FAILURE", label: "Only when a run fails" },
              { value: "OFF", label: "Off — don't email me" }
            ]} />
          </label>
        </div>

        <div className="ag-rail">
          <div className="ag-summary">
            <h4>⚡ What this agent does</h4>
            <ul className="ag-slist">
              {step(<span>Imports <b>{src.does}</b>{sourceType === "post" ? ` (${engagementType === "all" ? "reactions + comments" : engagementType})` : ""}, {runText}</span>)}
              {effAsk && step(<span>Asks you for the {src.urlLabel.toLowerCase()} each run</span>)}
              {step(<span>Skips anyone already processed or on DNC / undeliverable</span>)}
              {sequenceId && step(<span>Finds &amp; verifies emails (for email sequences)</span>)}
              {listId && step(<span>Adds them to <b>{listName}</b></span>)}
              {sequenceId && step(<span>Enrolls into <b>{sequenceName}</b></span>)}
              {notifyMode !== "OFF" && step(<span>{notifyMode === "DAILY_DIGEST" ? "Emails you a daily digest" : "Emails you when a run fails"}</span>)}
            </ul>
          </div>
          <button className="ag-btn ag-btn--primary" style={{ justifyContent: "center" }} disabled={!canSubmit || saving} onClick={submit}>
            {saving ? "Creating…" : "Create agent →"}
          </button>
          <button className="ag-btn" style={{ justifyContent: "center" }} disabled={saving} onClick={customize}>Customize steps →</button>
          <button className="ag-btn" style={{ justifyContent: "center" }} onClick={() => history.push("/agents")}>Cancel</button>
          {!canSubmit && <p className="ag-hint" style={{ textAlign: "center" }}>{!urlReady ? "Paste a LinkedIn URL to create — or “Customize steps” to edit the flow first." : "Pick a list to add the contacts to, or a sequence to enroll them in."}</p>}
        </div>
      </div>

      <SequencePickerModal
        open={seqModalOpen}
        currentId={sequenceId}
        onSelect={s => { setSequenceId(s.id); setSequenceName(s.name); }}
        onClose={() => setSeqModalOpen(false)}
      />
    </div>
  );
});

export default CreateLinkedInImport;
